import { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Plus, Settings2, Loader2, UserX, Lock, Search, StickyNote, Trash2, MonitorCheck, MonitorX } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { OrgChart } from "@/components/OrgChart";

const ROLE_STYLE = {
  owner: "bg-amber-50 text-amber-700 border-amber-200",
  admin: "bg-sky-50 text-sky-700 border-sky-200",
  supervisor: "bg-teal-50 text-teal-700 border-teal-200",
  office: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cleaner: "bg-slate-100 text-slate-600 border-slate-200",
};

const OA_LABEL = { CONNECT_ONLY: "Connect only", FIELD_ONLY: "Field only", CONNECT_FIELD: "Connect + Field" };
const OA_STYLE = { CONNECT_ONLY: "bg-sky-50 text-sky-700 border-sky-200", FIELD_ONLY: "bg-violet-50 text-violet-700 border-violet-200", CONNECT_FIELD: "bg-emerald-50 text-emerald-700 border-emerald-200" };

export default function Staff() {
  const { can, user: me } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", displayName: "", role: "cleaner", workerType: "static", languagePref: "en", password: "" });
  const [busy, setBusy] = useState(false);

  const [accessUser, setAccessUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [overrides, setOverrides] = useState({}); // key -> effect
  const [siteSel, setSiteSel] = useState([]); // siteIds
  const [supervisorId, setSupervisorId] = useState("");
  const [q, setQ] = useState("");
  const [oaFilter, setOaFilter] = useState("all");
  const [view, setView] = useState("list");
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [selected, setSelected] = useState({});
  const [batchBusy, setBatchBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      client.get("/users"),
      client.get("/sites").catch(() => ({ data: [] })),
      client.get("/meta/permissions"),
    ]).then(([u, s, m]) => {
      setUsers(u.data); setSites(s.data); setMeta(m.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await client.post("/users", form);
      toast.success(t("toast.userCreated","User created"));
      setCreateOpen(false);
      setForm({ email: "", displayName: "", role: "cleaner", workerType: "static", languagePref: "en", password: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openAccess = async (u) => {
    setAccessUser(u);
    setAccess(null);
    setNotes([]); setNoteBody("");
    try {
      const { data } = await client.get(`/users/${u.id}/access`);
      setAccess(data);
      const ov = {}; (data.overrides || []).forEach((o) => { ov[o.permissionKey] = o.effect; });
      setOverrides(ov);
      setSiteSel((data.siteAssignments || []).map((a) => a.siteId));
      setSupervisorId((data.supervisors || [])[0]?.supervisorUserId || "");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
    if (can("user.manage")) {
      client.get(`/users/${u.id}/private-notes`).then((r) => setNotes(r.data)).catch(() => {});
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await client.post(`/users/${accessUser.id}/private-notes`, { body: noteBody });
      setNoteBody("");
      const { data } = await client.get(`/users/${accessUser.id}/private-notes`);
      setNotes(data);
      toast.success(t("staff.noteSaved", "Note saved"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const delNote = async (id) => {
    if (!(await confirm({ title: t("staff.noteDelTitle", "Delete note"), description: t("staff.noteDelConfirm", "Delete this private note?"), confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try {
      await client.delete(`/users/${accessUser.id}/private-notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const toggleOverride = (key) => {
    setOverrides((prev) => {
      const cur = prev[key];
      const next = { ...prev };
      if (!cur) next[key] = "grant";
      else if (cur === "grant") next[key] = "deny";
      else delete next[key];
      return next;
    });
  };

  const saveAccess = async () => {
    setBusy(true);
    try {
      const ovList = Object.entries(overrides).map(([permissionKey, effect]) => ({ permissionKey, effect }));
      await client.put(`/users/${accessUser.id}/overrides`, ovList);
      await client.put(`/users/${accessUser.id}/sites`, siteSel.map((siteId) => ({ siteId, permissionLevel: "standard" })));
      if (supervisorId) await client.put(`/users/${accessUser.id}/supervisor`, { staffUserId: supervisorId, isPrimary: true });
      toast.success(t("toast.accessUpdated","Access updated"));
      setAccessUser(null);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePortal = async (u) => {
    try {
      const { data } = await client.put(`/users/${u.id}/portal-access`, { allowed: !u.portalAccess });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, portalAccess: data.portalAccess } : x)));
      toast.success(data.portalAccess ? `${u.displayName} can now use the portal` : `${u.displayName} can no longer use the portal`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const deactivate = async (u) => {
    try { await client.post(`/users/${u.id}/deactivate`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const canModify = useCallback((u) => u.id !== me?.id && (u.role !== "owner" || me?.role === "owner"), [me]);
  const shown = users.filter((u) => {
    const s = q.trim().toLowerCase();
    const matchQ = !s || [u.displayName, u.email, u.role].filter(Boolean).some((v) => v.toLowerCase().includes(s));
    const matchOA = oaFilter === "all" || (u.operationalAccess || "CONNECT_ONLY") === oaFilter;
    return matchQ && matchOA;
  });
  const selectableShown = shown.filter(canModify);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = selectableShown.length > 0 && selectableShown.every((u) => selected[u.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const next = {}; selectableShown.forEach((u) => { next[u.id] = true; }); setSelected(next); }
  };

  const batch = async (action) => {
    if (selectedIds.length === 0) return;
    const verb = action === "delete" ? t("staff.batchDeleteVerb", "permanently delete") : t("staff.batchDeactivateVerb", "deactivate");
    if (!(await confirm({ title: `${verb}`, description: t("staff.batchConfirm", `Are you sure you want to ${verb} ${selectedIds.length} selected user(s)?`).replace("{verb}", verb).replace("{n}", selectedIds.length), confirmText: verb, destructive: true }))) return;
    setBatchBusy(true);
    try {
      const { data } = await client.post(`/users/batch-${action === "delete" ? "delete" : "deactivate"}`, { userIds: selectedIds });
      const okCount = (data.deleted || data.deactivated || []).length;
      const skip = (data.skipped || []).length;
      toast.success(t("staff.batchDone", `${okCount} user(s) ${action === "delete" ? "deleted" : "deactivated"}${skip ? `, ${skip} skipped` : ""}.`));
      setSelected({});
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBatchBusy(false); }
  };

  const supervisors = users.filter((u) => ["supervisor", "admin", "owner"].includes(u.role));
  const roleDefaults = meta?.roleDefaults || {};

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Staff &amp; Access</h2>
          <p className="text-sm text-slate-500">{t("staff.subtitle","Roles, granular permissions, site assignments & supervisor links.")}</p>
        </div>
        {can("user.manage") && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="user-create-button" className="bg-brand hover:bg-brand-dark"><Plus className="h-4 w-4 mr-2" /> New user</Button>
            </DialogTrigger>
            <DialogContent data-testid="user-create-dialog">
              <DialogHeader><DialogTitle>{t("staff.createTitle","Create user")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("common.name","Name")}</Label><Input data-testid="user-name-input" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>{t("common.email","Email")}</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>{t("common.role","Role")}</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.roles || []).filter((r) => r !== "owner" || me?.role === "owner").map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("staff.lblWorkerType","Worker type")}</Label>
                    <Select value={form.workerType} onValueChange={(v) => setForm({ ...form, workerType: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.workerTypes || []).map((w) => <SelectItem key={w} value={w} className="capitalize">{w}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("staff.lblLanguage","Language")}</Label>
                    <Select value={form.languagePref} onValueChange={(v) => setForm({ ...form, languagePref: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.languages || []).map((l) => <SelectItem key={l} value={l} className="uppercase">{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>{t("staff.lblTempPass","Temporary password")}</Label><Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" /></div>
                <p className="text-[11px] text-slate-400">Worker type selects workflow only — it carries no authority.</p>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreateOpen(false)}>{t("common.cancel","Cancel")}</Button>
                <Button data-testid="user-save-btn" onClick={create} disabled={busy || !form.email || !form.displayName || !form.password} className="bg-brand hover:bg-brand-dark">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200">
        {loading ? <p className="p-8 text-slate-500 text-sm">Loading…</p> : (
          <>
          <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="staff-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("staff.search", "Search staff by name, email or role…")} className="pl-9 h-9" />
            </div>
            <Select value={oaFilter} onValueChange={setOaFilter}>
              <SelectTrigger data-testid="staff-oa-filter" className="h-9 w-[190px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("staff.oaAll", "All access")}</SelectItem>
                <SelectItem value="CONNECT_ONLY">Connect only</SelectItem>
                <SelectItem value="FIELD_ONLY">Field only</SelectItem>
                <SelectItem value="CONNECT_FIELD">Connect + Field</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center rounded-lg border border-slate-200 p-0.5" data-testid="staff-view-toggle">
              <button onClick={() => setView("list")} data-testid="staff-view-list" className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${view === "list" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>{t("staff.viewList", "List")}</button>
              <button onClick={() => setView("org")} data-testid="staff-view-org" className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${view === "org" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>{t("staff.viewOrg", "Org chart")}</button>
            </div>
            {can("user.manage") && selectedIds.length > 0 && (
              <div className="flex items-center gap-2" data-testid="batch-action-bar">
                <span className="text-sm text-slate-600 font-medium">{selectedIds.length} {t("staff.selected", "selected")}</span>
                <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" data-testid="batch-deactivate-btn" disabled={batchBusy} onClick={() => batch("deactivate")}>
                  <UserX className="h-4 w-4 mr-1.5" />{t("staff.deactivate", "Deactivate")}
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="batch-delete-btn" disabled={batchBusy} onClick={() => batch("delete")}>
                  {batchBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1.5" />{t("common.delete", "Delete")}</>}
                </Button>
                <Button size="sm" variant="ghost" data-testid="batch-clear-btn" onClick={() => setSelected({})}>{t("common.cancel", "Clear")}</Button>
              </div>
            )}
          </div>
          {view === "org" ? (
            <OrgChart users={users} onOpen={(uid) => navigate(`/staff/${uid}`)} />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {can("user.manage") && <TableHead className="h-9 py-2 w-8"><Checkbox data-testid="batch-select-all" checked={allSelected} onCheckedChange={toggleAll} /></TableHead>}
                <TableHead className="h-9 py-2">{t("common.name","Name")}</TableHead><TableHead className="h-9 py-2">{t("common.email","Email")}</TableHead><TableHead className="h-9 py-2">{t("common.role","Role")}</TableHead>
                <TableHead className="h-9 py-2">{t("staff.lblWorkerType","Worker type")}</TableHead><TableHead className="h-9 py-2">{t("staff.thOpAccess","Operational Access")}</TableHead><TableHead className="h-9 py-2">{t("staff.thLang","Lang")}</TableHead><TableHead className="h-9 py-2">{t("common.status","Status")}</TableHead><TableHead className="h-9 py-2">{t("staff.thPortal","Portal")}</TableHead><TableHead className="h-9 py-2"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/staff/${u.id}`)}>
                  {can("user.manage") && (
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox data-testid={`user-select-${u.id}`} disabled={!canModify(u)} checked={!!selected[u.id]}
                        onCheckedChange={(c) => setSelected((s) => ({ ...s, [u.id]: !!c }))} />
                    </TableCell>
                  )}
                  <TableCell className="py-2 font-semibold text-brand">
                    <span data-testid={`user-open-${u.id}`}>{u.displayName}</span>
                  </TableCell>
                  <TableCell className="py-2 text-slate-500">{u.email}</TableCell>
                  <TableCell className="py-2"><Badge variant="outline" className={`capitalize ${ROLE_STYLE[u.role]}`}>{u.role}</Badge></TableCell>
                  <TableCell className="py-2 capitalize text-slate-600">{u.workerType}</TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={OA_STYLE[u.operationalAccess] || OA_STYLE.CONNECT_ONLY} data-testid={`user-oa-${u.id}`}>{OA_LABEL[u.operationalAccess] || "Connect only"}</Badge>
                      {u.needsAccessReview && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid={`user-oa-review-${u.id}`}>Review</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 uppercase text-slate-600">{u.languagePref}</TableCell>
                  <TableCell className="py-2">
                    <Badge variant="outline" className={u.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>{u.status}</Badge>
                  </TableCell>
                  <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                    {u.role === "owner" ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid={`user-portal-owner-${u.id}`}>Always</Badge>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`user-portal-toggle-${u.id}`}
                        disabled={!can("user.manage") || !canModify(u)} onClick={() => togglePortal(u)}
                        title={u.portalAccess ? "Click to block portal access" : "Click to allow portal access"}
                        className={`h-7 px-2 text-xs ${u.portalAccess ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "text-slate-500 border-slate-200"}`}>
                        {u.portalAccess ? <MonitorCheck className="h-3.5 w-3.5 mr-1" /> : <MonitorX className="h-3.5 w-3.5 mr-1" />}
                        {u.portalAccess ? "Portal allowed" : "Access portal"}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                    {can("permission.manage") && (
                      <Button size="sm" variant="ghost" data-testid={`user-access-btn-${u.id}`} onClick={() => openAccess(u)}>
                        <Settings2 className="h-4 w-4 mr-1" /> Access
                      </Button>
                    )}
                    {can("user.manage") && u.status === "active" && u.role !== "owner" && (
                      <Button size="sm" variant="ghost" className="text-red-600" data-testid={`user-deactivate-btn-${u.id}`} onClick={() => deactivate(u)}>
                        <UserX className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          </>
        )}
      </Card>

      {/* Access management dialog */}
      <Dialog open={!!accessUser} onOpenChange={(o) => !o && setAccessUser(null)}>
        <DialogContent data-testid="access-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage access — {accessUser?.displayName}</DialogTitle></DialogHeader>
          {!accessUser ? null : !access ? <p className="text-slate-500 text-sm">Loading…</p> : (
            <Tabs defaultValue="permissions">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="permissions" data-testid="access-tab-permissions">{t("staff.tabPerms","Permissions")}</TabsTrigger>
                <TabsTrigger value="sites" data-testid="access-tab-sites">{t("staff.tabSites","Sites")}</TabsTrigger>
                <TabsTrigger value="supervisor" data-testid="access-tab-supervisor">{t("staff.tabSupervisor","Supervisor")}</TabsTrigger>
                <TabsTrigger value="notes" data-testid="access-tab-notes">{t("staff.tabNotes","Private Notes")}</TabsTrigger>
              </TabsList>

              <TabsContent value="permissions" className="space-y-2 pt-3">
                <p className="text-xs text-slate-500">Role default shown; click to cycle grant → deny → reset. Money permissions are Owner-only and locked.</p>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {(meta?.permissions || []).map((p) => {
                    const isDefault = (roleDefaults[accessUser?.role] || []).includes(p.key);
                    const ov = overrides[p.key];
                    const locked = p.money && accessUser?.role !== "owner";
                    const effective = locked ? false : (ov === "deny" ? false : ov === "grant" ? true : isDefault);
                    return (
                      <button key={p.key} data-testid={`perm-toggle-${p.key}`} disabled={locked}
                        onClick={() => !locked && toggleOverride(p.key)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left text-sm ${
                          locked ? "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed"
                          : effective ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}>
                        <span className="flex items-center gap-2">
                          {p.money && <Lock className="h-3.5 w-3.5 text-amber-500" />}
                          <span className="font-medium text-slate-700">{p.label}</span>
                          <span className="font-mono text-[10px] text-slate-400">{p.key}</span>
                        </span>
                        <span className="text-[10px] font-bold uppercase">
                          {locked ? "Owner-only" : ov ? <span className={ov === "grant" ? "text-emerald-600" : "text-red-600"}>{ov}</span> : (isDefault ? <span className="text-slate-400">default ✓</span> : <span className="text-slate-300">off</span>)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="sites" className="space-y-2 pt-3">
                <p className="text-xs text-slate-500">Assign sites this user can access. Supervisors/office are strictly scoped to these.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {sites.map((s) => (
                    <label key={s.id} data-testid={`site-assign-${s.id}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={siteSel.includes(s.id)} onCheckedChange={(c) => setSiteSel((prev) => c ? [...prev, s.id] : prev.filter((x) => x !== s.id))} />
                      <span className="text-sm text-slate-700">{s.name}</span>
                    </label>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="supervisor" className="space-y-2 pt-3">
                <p className="text-xs text-slate-500">Primary supervisor link. This never grants site or payroll access on its own.</p>
                <Select value={supervisorId} onValueChange={setSupervisorId}>
                  <SelectTrigger data-testid="supervisor-select"><SelectValue placeholder={t("staff.selectSupervisor","Select supervisor")} /></SelectTrigger>
                  <SelectContent>{supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.displayName} ({s.role})</SelectItem>)}</SelectContent>
                </Select>
              </TabsContent>

              <TabsContent value="notes" className="space-y-3 pt-3" data-testid="private-notes-tab">
                <p className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-amber-500" />{t("staff.notesHint", "Management-only. Never visible to the worker or in the mobile app. All changes are audited.")}</p>
                {can("user.manage") ? (
                  <>
                    <div className="flex gap-2">
                      <Input data-testid="private-note-input" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder={t("staff.notePlaceholder", "Add a private management note…")} />
                      <Button data-testid="private-note-add" onClick={addNote} disabled={!noteBody.trim()} className="bg-brand hover:bg-brand-dark">{t("common.add", "Add")}</Button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {notes.length === 0 ? <p className="text-sm text-slate-400">{t("staff.noNotes", "No private notes yet.")}</p> :
                        notes.map((n) => (
                          <div key={n.id} data-testid={`private-note-${n.id}`} className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-amber-100 bg-amber-50/40">
                            <div className="min-w-0">
                              <p className="text-sm text-slate-700 whitespace-pre-wrap flex items-start gap-1.5"><StickyNote className="h-3.5 w-3.5 mt-0.5 text-amber-500 shrink-0" />{n.body}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{n.authorName} · {new Date(n.createdAt).toLocaleString()}</p>
                            </div>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 shrink-0" data-testid={`private-note-del-${n.id}`} onClick={() => delNote(n.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        ))}
                    </div>
                  </>
                ) : <p className="text-sm text-slate-400">{t("staff.notesNoPerm", "You do not have permission to view private notes.")}</p>}
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAccessUser(null)}>{t("common.cancel","Cancel")}</Button>
            <Button data-testid="access-save-btn" onClick={saveAccess} disabled={busy} className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
